/**
 * useDeviceStateChanges — Sprint 4.2A.
 *
 * A cada atualização da lista `devices`, compara com a lista anterior
 * e detecta transições de estado, registrando em localStorage para
 * uso na Timeline.
 *
 * Transições rastreadas:
 *   - online <-> offline
 *   - relay null/on/off mudou
 *   - ota_state mudou
 *   - RSSI cruzou threshold (-80 dBm)
 *
 * Storage:
 *   localStorage key: ipwd-device-state-changes
 *   formato: { [device_id]: [ {ts, type, detail}, ... ] }
 *   máx 50 eventos por device (mais antigos são descartados)
 *
 * Limitações documentadas no README:
 *   - Eventos anteriores ao primeiro carregamento da página são perdidos.
 *   - Se localStorage for limpo, perde histórico de transições.
 *   - Não inclui eventos de outros operadores em outras sessões.
 *   - Comandos (relay/ota/reboot) vêm da API existente — esses persistem
 *     independente desta hook.
 */
import { useEffect, useRef } from "react";

const STORAGE_KEY = "ipwd-device-state-changes";
const MAX_PER_DEVICE = 50;
const RSSI_WEAK_THRESHOLD = -80;

function loadStorage() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function saveStorage(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Quota cheio — silently ignore (dados in-memory permanecem na próxima ref)
  }
}

/** Leitura externa para a Timeline modal. */
export function readDeviceStateEvents(deviceId) {
  if (!deviceId) return [];
  const all = loadStorage();
  return all[deviceId] || [];
}

/**
 * @param {Array} devices lista atual de FleetMapDeviceOut
 */
export function useDeviceStateChanges(devices) {
  const prevByIdRef = useRef(new Map());

  useEffect(() => {
    if (!devices || devices.length === 0) return;
    const prev = prevByIdRef.current;
    let changedAny = false;
    const all = loadStorage();
    const now = new Date().toISOString();

    for (const d of devices) {
      const old = prev.get(d.device_id);
      if (!old) {
        prev.set(d.device_id, d);
        continue;
      }
      const events = [];
      const ts = d.last_seen || now;

      if (old.online !== d.online) {
        events.push({
          ts,
          type: d.online ? "online" : "offline",
          detail: d.online ? "Device voltou online" : "Device ficou offline",
        });
      }
      const wasWeak = old.rssi != null && old.rssi < RSSI_WEAK_THRESHOLD;
      const isWeak = d.rssi != null && d.rssi < RSSI_WEAK_THRESHOLD;
      if (d.rssi != null && wasWeak !== isWeak) {
        events.push({
          ts,
          type: "rssi",
          detail: isWeak
            ? `RSSI fraco (${d.rssi} dBm)`
            : `RSSI normalizou (${d.rssi} dBm)`,
        });
      }
      if (old.relay !== d.relay && d.relay) {
        events.push({
          ts,
          type: d.relay === "on" ? "relay_on" : "relay_off",
          detail: `Relé reportado: ${String(d.relay).toUpperCase()}`,
        });
      }
      if (old.ota_state !== d.ota_state && d.ota_state) {
        events.push({
          ts,
          type: "ota",
          detail: `OTA state: ${d.ota_state}`,
        });
      }

      if (events.length > 0) {
        const arr = all[d.device_id] || [];
        all[d.device_id] = [...events, ...arr].slice(0, MAX_PER_DEVICE);
        changedAny = true;
      }

      prev.set(d.device_id, d);
    }

    if (changedAny) saveStorage(all);
  }, [devices]);
}
